import { Controller } from 'react-hook-form';
import FormFieldContainer from '../../../../reactForm/fields/FormFieldContainer';
import { assertSupportedSwarmTypes } from '../../swarmCompatibility';
import { getSwarmTypeOptionsOrThrow } from '../swarmTypesFieldContract';

interface SwarmTypesFieldSettings {
    default: unknown;
    options: Array<[string, string]>;
    readOnly?: boolean;
}

interface SwarmTypesFieldOptions {
    name: string;
    settings: SwarmTypesFieldSettings;
}

export default function SwarmTypesField({
    fieldOptions,
}: {
    fieldOptions: SwarmTypesFieldOptions;
}): JSX.Element {
    const { name, settings } = fieldOptions;
    const options = getSwarmTypeOptionsOrThrow(settings.options, `${name}.options`);
    const firstOption = options[0];
    const optionValues = options.map(([swarmType]) => swarmType);

    return (
        <Controller<Record<string, unknown>, string>
            name={name}
            defaultValue={settings.default}
            render={({ field: { value, onChange, onBlur } }) => {
                assertSupportedSwarmTypes(value, name);
                const selectedValues = value ?? [];
                const unsupportedValue = selectedValues.find(
                    (swarmType) => !optionValues.includes(swarmType)
                );
                if (unsupportedValue) {
                    throw new Error(
                        `Expected "${name}" value "${unsupportedValue}" to be a configured option.`
                    );
                }
                const selected = optionValues.filter((swarmType) =>
                    selectedValues.includes(swarmType)
                );
                const isGeneric = value === null;

                return (
                    <FormFieldContainer
                        fieldOptions={fieldOptions}
                        showOverrideCheckbox={false}
                        inline={false}
                    >
                        <div className="flex flex-col gap-2" onBlur={onBlur}>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name={`${name}-mode`}
                                    checked={isGeneric}
                                    disabled={settings.readOnly === true}
                                    onChange={() => onChange(null)}
                                />
                                {gettext('ALL_SENSOR_TYPES')}
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name={`${name}-mode`}
                                    checked={!isGeneric}
                                    disabled={settings.readOnly === true}
                                    onChange={() =>
                                        onChange(selected.length > 0 ? selected : [firstOption[0]])
                                    }
                                />
                                {gettext('SELECTED_SENSOR_TYPES')}
                            </label>
                            {!isGeneric ? (
                                <div className="ml-6 flex flex-wrap gap-x-4 gap-y-2">
                                    {options.map(([swarmType, label]) => {
                                        const isSelected = selected.includes(swarmType);
                                        const isLastSelected = isSelected && selected.length === 1;

                                        return (
                                            <label
                                                key={swarmType}
                                                className="flex items-center gap-2 text-sm"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    disabled={
                                                        settings.readOnly === true || isLastSelected
                                                    }
                                                    onChange={(event) => {
                                                        const nextSelected = new Set(selected);
                                                        if (event.target.checked) {
                                                            nextSelected.add(swarmType);
                                                        } else {
                                                            nextSelected.delete(swarmType);
                                                        }
                                                        onChange(
                                                            optionValues.filter((optionValue) =>
                                                                nextSelected.has(optionValue)
                                                            )
                                                        );
                                                    }}
                                                />
                                                {label}
                                            </label>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    </FormFieldContainer>
                );
            }}
        />
    );
}
